'use client'

import { useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'

/**
 * GA4 page_view 명시적 트래커
 *
 * @next/third-parties/google의 GoogleAnalytics 컴포넌트는 초기 로드 시
 * gtag('config')만 호출하고 App Router의 클라이언트 사이드 네비게이션을
 * 추적하지 못합니다. 또한 동적 라우트에서 document.title이 useEffect로
 * 뒤늦게 설정되면 초기 page_view가 빈 타이틀로 기록됩니다.
 *
 * 이 컴포넌트는 pathname/searchParams가 바뀔 때마다 title이 확정된 후
 * 명시적으로 page_view 이벤트를 전송하여 GA4 "페이지 및 스크린" 보고서에
 * 정확한 경로와 타이틀이 집계되도록 합니다.
 */
export function GoogleAnalyticsTracker({ gaId }: { gaId: string }) {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (!pathname || typeof window === 'undefined') return
    const gtag = (window as any).gtag
    if (typeof gtag !== 'function') return

    const query = searchParams?.toString()
    const page_path = query ? `${pathname}?${query}` : pathname
    const page_location = window.location.href

    // title이 동적 라우트에서 늦게 설정되는 경우를 대비해 다음 틱에 전송
    const timer = window.setTimeout(() => {
      gtag('event', 'page_view', {
        page_path,
        page_location,
        page_title: document.title,
        send_to: gaId,
      })
    }, 0)

    return () => window.clearTimeout(timer)
  }, [pathname, searchParams, gaId])

  return null
}
